import { get, post, put, remove } from '../Services/http';
import {
  dataLayerGtmViewCart,
  dataLayerRemoveFromCart,
} from '../Services/Gtm.service';
import { catchError } from './Error.action';
import {
  CART_LOAD_ERROR,
  CART_RECEIVE,
  CART_SHOW_INFO,
  CART_SET_GENERAL_ROW_NOTE,
  CART_ADD_TO_CART_ANIMATION,
} from '../constants';

export const load = () => (dispatch, getState) => {
  return get('/api/cart')
    .then((response) => response.json())
    .then((cart) => dispatch(receive(cart)))
    .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
};

export const loadError = (error) => ({
  type: CART_LOAD_ERROR,
  payload: {
    error,
  },
});

export const receive = (cart) => ({
  type: CART_RECEIVE,
  payload: cart,
});

// Flattens the orderRows object (keyed by rowGroupKey) into a single array of rows.
const flattenOrderRows = (orderRows) =>
  Object.keys(orderRows || {}).reduce(
    (all, key) => all.concat(orderRows[key] || []),
    []
  );

// Finds a single row by rowSystemId (preferred) or article number.
const findOrderRow = (orderRows, rowSystemId, articleNumber) =>
  flattenOrderRows(orderRows).find((row) =>
    rowSystemId
      ? row.rowSystemId === rowSystemId
      : row.articleNumber === articleNumber
  );

// Builds a GA4 remove_from_cart payload from cart rows and pushes it.
// removedQuantities lets a partial quantity decrease report the correct delta
// (keyed by rowSystemId); when omitted the row's full quantity is used.
const pushRemoveFromCart = (rows, removedQuantities = null) => {
  try {
    const items = (rows || [])
      .filter((row) => row && row.gtmEcommerceItem)
      .map((row) => {
        const removedQty =
          removedQuantities && removedQuantities[row.rowSystemId] != null
            ? removedQuantities[row.rowSystemId]
            : row.quantity;
        return { ...row.gtmEcommerceItem, quantity: removedQty };
      })
      .filter((item) => item.quantity > 0);

    if (items.length === 0) return;

    const value = items.reduce(
      (sum, item) => sum + (parseFloat(item.price) || 0) * item.quantity,
      0
    );
    dataLayerRemoveFromCart(items, items[0]?.currency, value);
  } catch (err) {
    console.error('Could not push remove_from_cart to datalayer');
  }
};

export const toggle = (value = null) => (dispatch, getState) => {
  if (value != null) {
    dispatch(show(value));
  } else {
    dispatch(show(!getState().cart.showInfo));
  }
};

const show = (visible) => ({
  type: CART_SHOW_INFO,
  payload: {
    showInfo: visible,
  },
});

export const update = (
  articleNumber,
  quantity,
  rowSystemId,
  abortController = null
) => (dispatch, getState) => {
  // Capture the row before the update so we can report a remove_from_cart delta
  // when the quantity is decreased (or the row is removed via quantity 0).
  const existingRow = findOrderRow(
    getState().cart.orderRows,
    rowSystemId,
    articleNumber
  );
  const oldQuantity = existingRow ? parseFloat(existingRow.quantity) : null;

  return put(
    `/api/cart/update`,
    { articleNumber, quantity, rowSystemId },
    abortController
  )
    .then((response) => response.json())
    .then((cart) => {
      const removedQty =
        oldQuantity != null ? oldQuantity - parseFloat(quantity) : 0;
      if (existingRow && removedQty > 0) {
        pushRemoveFromCart([existingRow], {
          [existingRow.rowSystemId]: removedQty,
        });
      }
      dispatch(receive(cart));
    })
    .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
};

export const removeOrderRowGroup = (rowGroupKey, abortController = null) => (
  dispatch,
  getState
) => {
  const removedRows = getState().cart.orderRows?.[rowGroupKey] || [];
  return remove(`/api/cart/rowgroup/${rowGroupKey}`, null, abortController)
    .then((response) => response.json())
    .then((cart) => {
      pushRemoveFromCart(removedRows);
      dispatch(receive(cart));
    })
    .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
};

export const clear = (abortController = null) => (dispatch, getState) => {
  const removedRows = flattenOrderRows(getState().cart.orderRows);
  return post(`/api/cart/clear`, null, abortController)
    .then((response) => response.json())
    .then((cart) => {
      pushRemoveFromCart(removedRows);
      dispatch(receive(cart));
    })
    .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
};

export const setRowNote = (rowSystemId, note, abortController = null) => (
  dispatch
) => {
  return post(
    `/api/cart/rows/${rowSystemId}/note?note=${note}`,
    abortController
  )
    .then((response) => response.json())
    .then((cart) => dispatch(receive(cart)))
    .catch((ex) => dispatch(catchError(ex, (error) => loadError(error))));
};

export const setGeneralRowNote = (generalRowNote) => ({
  type: CART_SET_GENERAL_ROW_NOTE,
  payload: {
    generalRowNote,
  },
});

var addToCartAnimationTimeoutId;
export const triggerAddToCartAnimation = () => (dispatch, getState) => {
  const addToCartAnimating = getState().cart.addToCartAnimating;
  if (!addToCartAnimating) {
    if (addToCartAnimationTimeoutId) {
      clearTimeout(addToCartAnimationTimeoutId);
    }

    dispatch(setAddToCartAnimating(true));
    addToCartAnimationTimeoutId = setTimeout(
      () => dispatch(setAddToCartAnimating(false)),
      500
    );
  }
};

const setAddToCartAnimating = (addToCartAnimating) => ({
  type: CART_ADD_TO_CART_ANIMATION,
  payload: {
    addToCartAnimating,
  },
});

export const triggerDataLayerAddToCart = (orderRows, grandTotal) => (dispatch, getState) => {
  try {
    let gtmOrderRows = [];
    Object.keys(orderRows).map((rowGroupKey) =>
      orderRows[rowGroupKey].map((row) => {
        row.gtmEcommerceItem.quantity = row.quantity;
        gtmOrderRows.push(row)
      })
    );
    
    if (gtmOrderRows.length > 0) {
      dataLayerGtmViewCart({
        currency: gtmOrderRows[0].gtmEcommerceItem.currency,
        gtmGrandTotal: grandTotal,
        orderRows: gtmOrderRows
      });
    }
  } catch (err) {
    console.error('Could not add view cart to datalayer');
  }
};
